import React, { useEffect, useState, Suspense } from "react";
import ReactDOM, { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import { lazyWithRetry } from "./helpers/lazyWithRetry";
import "./utilities.scss";
// @TODO: move donations to its own file to prevent importing css unnecessarily
import { BrowserRouter as Router } from "react-router-dom";
import { Navigate, Route, Routes } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { createStore, StateMachineProvider } from "little-state-machine";
import { ToastContainer, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import AppContext, { useAppContext } from "./hooks/useAppContext";
import { setWithExpiry } from "./helpers/localStorageExpiry";

import {
  APP_STORAGE_NAMESPACE,
  COMMERCE_APP_RELEASE,
  COMMERCE_CART_ITEMCOUNT,
  COMMERCE_ORDER_APPEALCODE,
  COMMERCE_CHECKOUT_DETAILS,
  COMMERCE_WEBFORM_TOKEN,
} from "./storage/constants";

import CheckoutRoute from "./CheckoutRoute";
import MiniDonations from "./MiniDonations";
import FundCardPortal from "./portals/FundCardPortal";
import {
  getCampaignFundSelected,
  getWebformId,
  getWebformPath,
  getWebformToken,
} from "./services/funds.service";
import useOrderConfigParams from "./hooks/useOrderConfigParams";
import { clearStorage } from "./services/storage.service";

const Checkout = lazyWithRetry(() => import("./Components/Checkout"));
const CheckoutYourInfo = lazyWithRetry(
  () => import("./Components/CheckoutYourInfo"),
);
const CheckoutPersonalInfo = lazyWithRetry(
  () => import("./Components/CheckoutPersonalInfo"),
);
const CheckoutHonoreeInfo = lazyWithRetry(
  () => import("./Components/CheckoutHonoreeInfo"),
);
const CheckoutSpouseInfo = lazyWithRetry(
  () => import("./Components/CheckoutSpouseInfo"),
);
const FundSideBar = lazyWithRetry(() => import("./Components/FundSideBar"));
const FundCategory = lazyWithRetry(() => import("./Components/FundCategory"));
const ThankYouPage = lazyWithRetry(() => import("./Components/ThankYouPage"));
const GivingHeader = lazyWithRetry(() => import("./Components/GivingHeader"));
const CheckoutPayment = lazyWithRetry(
  () => import("./Components/CheckoutPayment"),
);
const ManageGift = lazyWithRetry(() => import("./Components/ManageGift"));

const release = RELEASE;
console.debug("RELEASE", RELEASE);
console.debug("CLEAR_LOCAL_STORAGE", CLEAR_LOCAL_STORAGE);
console.debug("NODE_ENV", process.env.NODE_ENV);
console.debug("SENTRY_ENVIRONMENT", process.env.SENTRY_ENVIRONMENT);

Sentry.init({
  dsn: "https://c76feb3947ee4e06b9465352694e1b59@o139504.ingest.sentry.io/5619479",
  release: `emoryaae-react@${release}`,
  attachStacktrace: "on",
  environment: process.env.SENTRY_ENVIRONMENT,
  ignoreErrors: ["Non-Error promise rejection captured"],
});

createStore(
  {
    checkoutDetails: {
      // Your Info
      prefix: "",
      firstName: "",
      lastName: "",
      yourEmail: "",
      suffix: "",
      donationOptions: "",
      jointGift: "",
      // Personal Info
      phoneType: "",
      country: "US",
      state: "AK",
      // Billing info
      billToPersonalAddress: true,
      billingCountry: "US",
      billingState: "AK",
      // Honoree Info
      toPrefix: "",
      toFirstName: "",
      toMiddleName: "",
      toLastName: "",
      toSuffix: "",
      // Mail a Notification info
      mailState: "AK",
      mailCountry: "US",
      // Spouse Info
      spousePrefix: "",
      spouseFirstName: "",
      spouseMiddleName: "",
      spouseLastName: "",
      spouseSuffix: "",
      // Payment Info
      paymentType: "",
    },
  },
  {
    name: COMMERCE_CHECKOUT_DETAILS,
  },
);

function clearLocalStorage() {
  // Clear legacy unnamespaced app storage items.
  // @todo remove this after website launch
  let legacyItemsCleared = false;
  [
    "commerce-cart-token",
    "initial-query-params",
    "cart-count-items",
    "Emory.commerce.appeal",
    "__LSM__",
  ].map((legacyItem) => {
    if (localStorage.getItem(legacyItem) !== null) {
      localStorage.removeItem(legacyItem);
      legacyItemsCleared = true;
    } else if (sessionStorage.getItem(legacyItem) !== null) {
      sessionStorage.removeItem(legacyItem);
      legacyItemsCleared = true;
    }
  });
  legacyItemsCleared && console.debug("App storage legacy items cleared.");

  // Clear all commerce app storage after each release.
  if (
    RELEASE !== localStorage.getItem(COMMERCE_APP_RELEASE) &&
    CLEAR_LOCAL_STORAGE &&
    CLEAR_LOCAL_STORAGE.includes("TRUE")
  ) {
    clearStorage();
    console.debug("App storage cleared for new release.", RELEASE);
    localStorage.setItem(COMMERCE_APP_RELEASE, RELEASE);
  }
}
clearLocalStorage();

// Save appeal code for later.
const urlParams = new URLSearchParams(location.search);
const appeal =
  urlParams.get("appeal") ||
  urlParams.get("appealcode") ||
  urlParams.get("appeal_code");

// Edit Webforms attached to Donations.
const urlToken = urlParams.get("token");
const webformToken = getWebformToken();
const webformPath = getWebformPath();

if (
  location.pathname === webformPath &&
  webformToken &&
  location.href.indexOf(webformToken) === -1
) {
  location.replace(webformPath + "?token=" + webformToken);
}

if (urlToken && !webformToken) {
  location.replace(location.pathname);
}

if (appeal) {
  const oneWeek = 604.8e6;
  setWithExpiry(COMMERCE_ORDER_APPEALCODE, appeal, oneWeek);
  console.debug(COMMERCE_ORDER_APPEALCODE, appeal);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ emoryAppealCode: appeal });
}

// Workaround for EAE-1093.
// Reroute specific appeals to another page. We use location.replace()
// to prevent items appearing in browser history.  We also cannot use a
// <Redirect to={reroute_appeals[appeal]} /> because the App requires a
// complete page reload and round-trip from server to properly load the
// taxonomy term category pages (for some unknown reason outside the
// scope of this quickfix). Refer to EAE-1093 for when it is safe to
// remove this code.
const reroute_appeals = {
  CDAE2: "/taxonomy/term/421",
  CEAE2: "/taxonomy/term/421",
  CDBE2: "/taxonomy/term/421",
  CEBE2: "/taxonomy/term/421",
  CDCE2: "/taxonomy/term/421",
  CECE2: "/taxonomy/term/421",
  CDDE2: "/taxonomy/term/421",
  CEDE2: "/taxonomy/term/421",
  CDEE2: "/taxonomy/term/421",
  CEEE2: "/taxonomy/term/421",
  CDFE2: "/taxonomy/term/421",
  CEFE2: "/taxonomy/term/421",
  CDAD2: "/taxonomy/term/421",
  "2VWGT": "/taxonomy/term/421",
  CNGD2: "/taxonomy/term/421",
  CNHD2: "/taxonomy/term/421",
  CNID2: "/taxonomy/term/421",
  CNJD2: "/taxonomy/term/421",
  CNKD2: "/taxonomy/term/421",
  // End workaround for EAE-1093.

  // Workaround for EAE-1320.
  "2DOGE": "/give/checkout?cc=1&aid=10560001.03009501.24714002.24225004",
  "2EOAR": "/give/checkout?cc=1&aid=10560001.03009501.24714002.24225004",
  // End workaround for EAE-1320.
};
if (appeal && reroute_appeals.hasOwnProperty(appeal)) {
  const params = new URLSearchParams(location.search);
  params.delete("appeal_code");
  params.delete("appeal");
  params.delete("appealcode");
  if (reroute_appeals[appeal].includes("?")) {
    location.replace(
      decodeURIComponent(
        `${reroute_appeals[appeal]}&${params.toString().split("?")[0]}`,
      ),
    );
  } else {
    location.replace(
      decodeURIComponent(`${reroute_appeals[appeal]}?${params}`),
    );
  }
}

// @todo refactor portals out of index.js
function GivingApp(props) {
  const [fundCategoryId, setFundCategoryId] = useState(props.fundCategoryId);
  // Generate the token for anonymous users.
  const { toggleMenu, isCheckoutEnabled, shouldGoThankYou, givingApp } =
    useAppContext();

  // @todo: remove hack that prevents back button navigation (out of react events control)
  const onBackButtonEvent = (e) => {
    e.preventDefault();

    if (!isCheckoutEnabled && location.pathname.includes("checkout/")) {
      window.location.href = "/give/checkout";
    }

    if (shouldGoThankYou && location.pathname.includes("checkout/")) {
      window.location.href = "/give/thank-you";
    }
  };

  // @todo document how this works
  useEffect(() => {
    window.addEventListener("popstate", onBackButtonEvent);

    return () => {
      window.removeEventListener("popstate", onBackButtonEvent);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return createPortal(
    <>
      <Router>
        <Routes>
          <Route
            path="/give"
            element={
              <FundCategory
                setFundCategoryId={setFundCategoryId}
                fundCategoryId={fundCategoryId}
              />
            }
          />
          <Route
            path="/give/to/*"
            element={
              <FundCategory
                setFundCategoryId={setFundCategoryId}
                fundCategoryId={fundCategoryId}
              />
            }
          />
          <Route
            path="/give/checkout"
            element={
              <>
                <Sidebar />
                <GivingHeader type={"dual-branded"} />
                <Checkout checkoutForm={<CheckoutYourInfo />} />
              </>
            }
          />
          <Route
            path="/give/checkout/personal-information"
            element={
              <CheckoutRoute>
                <GivingHeader type={"dual-branded"} />
                <Checkout checkoutForm={<CheckoutPersonalInfo />} />
              </CheckoutRoute>
            }
          />
          <Route
            path="/give/checkout/honoree-information"
            element={
              <CheckoutRoute>
                <GivingHeader type={"dual-branded"} />
                <Checkout checkoutForm={<CheckoutHonoreeInfo />} />
              </CheckoutRoute>
            }
          />
          <Route
            path="/give/checkout/spouse-information"
            element={
              <CheckoutRoute>
                <GivingHeader type={"dual-branded"} />
                <Checkout checkoutForm={<CheckoutSpouseInfo />} />
              </CheckoutRoute>
            }
          />
          <Route
            path="/give/checkout/payment"
            element={
              <CheckoutRoute>
                <GivingHeader type={"dual-branded"} />
                <Checkout checkoutForm={<CheckoutPayment />} />
              </CheckoutRoute>
            }
          />
          <Route
            path="/give/thank-you"
            element={
              <>
                {!isCheckoutEnabled && !shouldGoThankYou && (
                  <Navigate to="/give/checkout" replace />
                )}
                <GivingHeader type={"shield"} />
                <ThankYouPage />
              </>
            }
          />
        </Routes>
      </Router>
    </>,
    givingApp,
  );
}

function ManageGiftApp({ recurringOrderUuid }) {
  const { manageGiftApp } = useAppContext();

  return createPortal(
    <>
      <Router>
        <Routes>
          <Route
            path="/give/manage-gift/:recurring_order_uuid/:token"
            element={
              <>
                <GivingHeader type={"dual-branded"} />
                <ManageGift recurringOrderUuid={recurringOrderUuid} />
              </>
            }
          />
        </Routes>
      </Router>
    </>,
    manageGiftApp,
  );
}

function Sidebar() {
  const { toggleMenu, innerWidth } = useAppContext();
  const [cartItemCount, setCartItemCount] = useState(0);
  const shouldLoad =
    !["thank", "checkout"].some((i) => location.pathname.includes(i)) ||
    (location.pathname.includes("checkout") && innerWidth < 768);

  useEffect(() => {
    const onStorage = (storage) => {
      if (storage.key === COMMERCE_CART_ITEMCOUNT && storage.newValue) {
        setCartItemCount(parseInt(storage.newValue, 10));
      }

      if (storage.key === COMMERCE_WEBFORM_TOKEN && storage.newValue) {
        getCampaignFundSelected() === null
          ? // We dont want to redirect when user fills out the webform
            // ? location.replace("/give/checkout")
            ""
          : document
              .querySelector(`[data-fund-uuid="${getCampaignFundSelected()}"]`)
              .getElementsByClassName("fund-card__button")[0]
              .click();
      }
    };

    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    const cart = document.getElementsByClassName("header-cart-button")[0];
    if (!shouldLoad || cartItemCount === 0) {
      cart.classList.add("is-empty");
    }

    if (shouldLoad && cartItemCount > 0) {
      cart.classList.remove("is-empty");
      cart.textContent = cartItemCount;
    }
  }, [shouldLoad, cartItemCount]);

  return (
    (shouldLoad && (
      <>
        <button id={"toggle-sidebar"} onClick={toggleMenu} hidden>
          Toggle cart sidebar
        </button>
        <FundSideBar />
      </>
    )) ||
    null
  );
}

function SidebarCartApp() {
  const { sidebarCartApp } = useAppContext();
  return createPortal(<Sidebar />, sidebarCartApp);
}

// We open the sidebar when we click on the div with the number of items.
let button = document.getElementsByClassName("header-cart-button");
if (button.length > 0) {
  button[0].addEventListener("click", () => {
    document.getElementById("toggle-sidebar").click();
  });
}

function App() {
  const {
    fundCategoryId,
    sidebarCartApp,
    givingApp,
    manageGiftApp,
    recurringOrderUuid,
    miniCartApp,
    fundCardApp,
  } = useAppContext();
  useOrderConfigParams(true);

  useEffect(() => {
    const loader = document.querySelector("#giving-app > h2.loading-text");
    loader?.remove();
  }, []);

  return (
    <>
      {sidebarCartApp && <SidebarCartApp />}
      {givingApp && <GivingApp fundCategoryId={fundCategoryId} />}
      {manageGiftApp && (
        <ManageGiftApp recurringOrderUuid={recurringOrderUuid} />
      )}
      {miniCartApp.map((e, i) => {
        return (
          e && (
            <MiniDonations
              key={i}
              element={e}
              fundId={e.getAttribute("data-fund-id")}
            />
          )
        );
      })}
      {fundCardApp.map((e, i) => {
        let description = null;
        if (!e.parentNode.hasAttribute("data-hide-description")) {
          description = e.parentNode.getAttribute("data-description");
          if (!description) {
            description = e.getAttribute("data-fund-description");
          }
        }
        return (
          e && (
            <FundCardPortal
              key={i}
              element={e}
              fundUUID={e.getAttribute("data-fund-uuid")}
              frequency={e.parentNode.getAttribute("data-frequency")}
              fundTitle={e.getAttribute("data-fund-title")}
              fundDescription={description}
              hideHonoree={e.parentNode.hasAttribute("data-hide-honoree")}
              honoree={e.parentNode.getAttribute("data-honoree")}
              hidePledge={e.parentNode.hasAttribute("data-hide-pledge")}
              isPledge={e.parentNode.hasAttribute("data-is-pledge-payment")}
              hideJointGift={e.parentNode.hasAttribute("data-hide-joint-gift")}
            />
          )
        );
      })}

      <ToastContainer
        position="bottom-center"
        transition={Slide}
        hideProgressBar
        autoClose={false}
        closeOnClick={false}
        closeButton={false}
        toastClassName={({ type }) =>
          `messages messages--toast messages--${type}`
        }
        bodyClassName={() => "messages__body"}
        style={{ width: "initial" }}
      />
    </>
  );
}

const container = document.getElementById("react-root");
const root = createRoot(container);
root.render(
  <StateMachineProvider>
    <AppContext>
      <Suspense fallback={null}>
        <App />
      </Suspense>
    </AppContext>
  </StateMachineProvider>,
);
